import { useState } from 'react';
import type { ApprovalDocumentDetail, ApprovalLineDetail } from '../../types/approval';
import { stampImageUrl } from '../../api/approval-signature';
import { SignatureModal, NameStamp } from './SignatureModal';
import './SignatureModal.css';
import { approvalLines, lineSlotLabel } from '../../lib/approval-line-slot';

/**
 * 결재 시트의 **도장 줄** (2026-08-28 · 자리 재정의 판 8).
 *
 * ★★ **도장은 「누가 승인했나」를 한눈에 보이게 한다.** 지금까지는 단계 목록에 글자로만 있어,
 *    종이 결재란에 익숙한 사람에게는 「결재를 받았다」는 느낌이 없었다.
 *
 * ★★ **찍힌 것만 그린다.** 아직 안 온 칸은 빈 자리로 둔다 — 미리 그려 두면 「이미 받았나」로 읽힌다.
 * ★★ **여기 있는 도장은 그때 찍힌 것**이다. 그 사람이 나중에 서명을 바꿔도 이 그림은 안 바뀐다.
 *
 * ★ 참고자 줄은 도장을 안 그린다 — 참고자는 승인이 아니다(줄 목록에는 그대로 선다).
 * ★ 내 칸에서 **즉석으로** 서명을 만든다 — 다른 화면으로 보내지 않는다.
 */

/*
 * ⚠ **2026-09-04(판 P1)** — 「결재」 한 낱말로는 자리를 못 말한다. 결재 줄이 여럿이면
 *   **마지막 하나만 「최종결재자」**이고 그 앞은 「결재자」다. 셈은 `lib/approval-line-slot.ts` 한 곳.
 */

/**
 * 도장 기록 없이 **이미 처리된** 줄에 적는 말 (2026-08-29).
 *
 * ★★ 판 8 **이전에 처리된 줄은 `stamp` 가 없다.** 그런데 빈 자리로 그리면 「아직 안 왔다」로
 *    읽힌다 — 바로 아래 결재선에는 「승인 · 2026. 8. 25.」라고 적혀 있는데도 그렇다.
 *    실제로 운영 문서에서 그 상태였다(승인된 줄인데 결재란은 빈 동그라미).
 * ★ **도장을 지어내지 않는다.** 그때 무엇이 찍혔는지는 기록에 없으므로, 도장 모양이 아니라
 *   **글자**로 「승인/반려/건너뜀」만 적는다 — 있는 사실만 말한다.
 */
const PROCESSED_MARK: Record<string, string> = {
  APPROVED: '승인', REJECTED: '반려', SKIPPED: '건너뜀',
};

interface Props {
  doc: ApprovalDocumentDetail;
  /** 내 사용자 id — 내 칸에 「내 서명」을 붙인다. 모르면 안 붙인다. */
  myUserId?: string | null;
  myName?: string;
  /** 서명을 바꾸면 부르는 쪽이 문서를 다시 읽는다(다음 결재부터 그 도장이 찍힌다). */
  onSignatureChanged?: () => void;
}

export function ApprovalStampRow({ doc, myUserId, myName, onSignatureChanged }: Props) {
  const [editing, setEditing] = useState(false);

  // 참고자는 승인이 아니라 도장 칸을 안 만든다.
  const lines = approvalLines(doc.lines);
  if (lines.length === 0) return null;

  return (
    <section className="approval-detail__lines" aria-label="결재란">
      <h3>결재란</h3>
      <div className="stamprow">
        {lines.map((l, i) => (
          <StampBox
            key={l.id}
            doc={doc}
            line={l}
            // ★ 자리 이름은 순서가 정한다 — 마지막 결재 줄 하나만 「최종결재자」다(판 P1).
            slot={lineSlotLabel(lines, i)}
            mine={myUserId != null && l.approver?.userId === myUserId}
            onEditSignature={() => setEditing(true)}
          />
        ))}
      </div>

      {editing && (
        <SignatureModal
          myName={myName ?? ''}
          onClose={() => setEditing(false)}
          onSaved={() => onSignatureChanged?.()}
        />
      )}
    </section>
  );
}

function StampBox({ doc, line, slot, mine, onEditSignature }: {
  doc: ApprovalDocumentDetail;
  line: ApprovalLineDetail;
  /** 결재자 · 최종결재자 · 합의 — 부르는 쪽이 순서로 셈해서 준다(`lib/approval-line-slot.ts`). */
  slot: string;
  mine: boolean;
  onEditSignature: () => void;
}) {
  const role = slot;
  const name = line.stamp?.name || line.approver?.name || `결재자 ${line.approverId}`;
  const rejected = line.status === 'REJECTED';
  /** 도장은 없는데 이미 처리된 줄인가 — 그때는 빈 자리가 아니라 무슨 처리였는지를 적는다. */
  const processedMark = line.stamp ? null : PROCESSED_MARK[line.status] ?? null;

  return (
    <div className={`stampbox${rejected ? ' is-rejected' : ''}`}>
      <span className="stampbox__role">{role}</span>
      <span className="stampbox__slot">
        {/*
          ★ 찍힌 것이 있을 때만 그린다. 세 갈래다 —
            · 그림 도장 → 그 줄의 그림(문서마다 다를 수 있다)
            · 이름 도장 → 글자로 그린다(서버에 그림이 없다)
            · 아직 안 찍힘 → 빈 자리
        */}
        {line.stamp?.hasImage ? (
          <img
            className="stampbox__img"
            src={stampImageUrl(doc.id, line.id)}
            alt={`${name} 도장`}
          />
        ) : line.stamp ? (
          <NameStamp name={line.stamp.name} size={56} />
        ) : processedMark ? (
          /* 도장 기록은 없지만 이미 처리된 줄(판 8 이전) — 빈 자리로 두면 「아직 안 왔다」가 된다. */
          <span className="stampbox__past" aria-label={`${name} ${processedMark} — 도장 기록 없음`}>
            {processedMark}
          </span>
        ) : (
          <span className="stampbox__wait" aria-label="아직 처리되지 않음" />
        )}
      </span>
      <span className="stampbox__name">{name}</span>
      {mine && (
        <button type="button" className="stampbox__mine" onClick={onEditSignature}>
          내 서명
        </button>
      )}
    </div>
  );
}
